import React, { Component, ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { user } from 'ampersand-app'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import ButtonSquare from '@/modules/base/components/buttons/square/button-square'
import { makeStyles } from '@mui/styles'
import { capturePostHogException } from '@/modules/posthog/posthog-tracking'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

interface ErrorBoundaryProps extends Props {
  location: string
  classes: {
    errorContainer: string
    reloadButton: string
    errorDetails: string
    errorStack: string
    errorComponent: string
  }
}

const useStyles = makeStyles((theme) => ({
  // eslint-disable-next-line mui-unused-classes/unused-classes
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
    padding: '20px',
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  reloadButton: {
    width: '145px !important',
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  errorDetails: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '16px',
    margin: '16px 0',
    maxWidth: '800px',
    width: '100%',
    fontFamily: 'monospace',
    fontSize: '14px',
    overflow: 'auto',
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  errorStack: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '12px',
    margin: '8px 0',
    whiteSpace: 'pre-wrap',
    fontSize: '12px',
    maxHeight: '300px',
    overflow: 'auto',
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  errorComponent: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '12px',
    margin: '8px 0',
    color: '#856404',
  },
}))

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo)

    // Update state with error details
    this.setState({ error, errorInfo })

    // Show toast notification with default error message
    toast.showToast({ type: 'error', message: lang.defaultErrorMessage })

    // Log to APM if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).elasticApm) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).elasticApm.captureError(error)
    }

    // React swallows boundary errors in production, so they never reach window.onerror
    capturePostHogException(error, { source: 'ErrorBoundary', componentStack: errorInfo.componentStack })
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when location changes (but not to dashboard to avoid loops)
    if (prevProps.location !== this.props.location && this.state.hasError) {
      if (this.props.location !== '/dashboard') {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined })
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // In development mode, show detailed error information
      if (process.env.NODE_ENV === 'development') {
        return (
          <div className={this.props.classes.errorContainer}>
            <h2>🚨 Development Error</h2>
            <p>An error occurred in your React component. Check the details below:</p>

            <div className={this.props.classes.errorDetails}>
              <h3>Error Message:</h3>
              <div className={this.props.classes.errorStack}>
                {this.state.error?.message || 'Unknown error'}
              </div>

              <h3>Component Stack:</h3>
              <div className={this.props.classes.errorStack}>
                {this.state.errorInfo?.componentStack || 'No component stack available'}
              </div>

              <h3>Error Stack:</h3>
              <div className={this.props.classes.errorStack}>
                {this.state.error?.stack || 'No stack trace available'}
              </div>
            </div>

            <div className={this.props.classes.errorComponent}>
              <strong>💡 Tip:</strong> Check the component that caused this error and fix the issue.
              The error boundary will automatically recover when you navigate to a different route.
            </div>

            <ButtonSquare
              onClick={() => window.location.reload()}
              label="Reload Page"
              className={this.props.classes.reloadButton}
              dataQsId='error-boundary-reload'
            />
          </div>
        )
      }

      // Production mode - existing navigation logic
      // Check if user is logged in
      if (!user?.loggedIn) {
        // User is not logged in, redirect to login
        return <Navigate to="/login" replace />
      }

      // User is logged in, redirect to dashboard if not already there
      if (this.props.location !== '/dashboard') {
        return <Navigate to="/dashboard" replace />
      }

      // If we're already on dashboard and there's an error, show a simple error message
      return (
        <div className={this.props.classes.errorContainer}>
          <h2>{lang.errorBoundary.title}</h2>
          <p>{lang.errorBoundary.description}</p>
          <ButtonSquare
            onClick={() => window.location.reload()}
            label={lang.errorBoundary.reloadButton}
            className={this.props.classes.reloadButton}
            dataQsId='error-boundary-reload'
          />
        </div>
      )
    }

    return this.props.children
  }
}

// Wrapper component to provide location and styles
const ErrorBoundary = ({ children }: Props) => {
  const location = useLocation()
  const classes = useStyles()
  return <ErrorBoundaryClass location={location.pathname} classes={classes}>{children}</ErrorBoundaryClass>
}

export default ErrorBoundary
